<script setup>

    import { ref, watch, computed, nextTick } from 'vue'
    import { useRoute } from 'vue-router'
    import { storeToRefs } from 'pinia'
    import { MagnifyingGlassIcon } from '@heroicons/vue/24/solid'
    import { ArrowLongRightIcon, ExclamationCircleIcon, XCircleIcon } from '@heroicons/vue/20/solid'
    import { IconEditOff24Filled } from '@iconify-prerendered/vue-fluent'
    import { deepCopy } from '@/Helpers'
    import { useCaregiverSingleStore, useConfigStore } from '@/Stores'
    import LatteCaregiverCriteriaAttributes from '@/Components/Caregiver/LatteCaregiverCriteriaAttributes.vue'
    import ProgressCircle from '@/Components/ProgressCircle.vue'


    const emit = defineEmits(['update:modelValue', 'update:selected-criteria-summary', 'close'])

    const props = defineProps({
        criteriaDescriptors: {
            type: Array,
            required: true,
        },
        categories: {
            type: Array,
            required: true,
        },
        certifications: {
            type: Array,
            required: false,
        },
        modelValue: {
            type: Array,
            required: true,
        }
    })

    const severityCriteriaNameIds = ['alzheimers_disease', 'dementia']
    const foreignLanguageCriteriaNameIds = ['foreign_language_basic', 'foreign_language_fluent']

    const configStore = useConfigStore()
    const caregiverSingleStore = useCaregiverSingleStore()
    const route = useRoute()
    const { caregiver, loading } = storeToRefs(caregiverSingleStore)
    const { criteriaNameDict: criteriaDescriptorNameDict } = storeToRefs(configStore)

    const input = ref(null)
    const searchInput = ref(null)
    const filterCategories = ref([])
    const categoryRefs = ref({})
    const criteriaRefs = ref({})
    const mainPanel = ref(null)

    function clearSearch(focusSearchInput = true) {
        input.value = null
        if (focusSearchInput) {
            searchInput.value.focus()
        }
    }

    function setCriteriaRef(name_id, el) {
        if (el) {
            criteriaRefs.value[name_id] = el
        }
    }

    function setCategoryRef(id, el) {
        if (el) {
            categoryRefs.value[id] = el
        }
    }

    function scrollToFirstIncomplete() {
        clearSearch(false)
        let topCriteriaId = null
        let minTop = Infinity

        missingRequiredAttribute1stLevelCriterias.value.forEach(criteriaNameId => {
            const el = criteriaRefs.value[criteriaNameId]
            if (el) {
                const rect = el.getBoundingClientRect()
                const absoluteTop = rect.top + window.pageYOffset

                if (absoluteTop < minTop) {
                    minTop = absoluteTop
                    topCriteriaId = criteriaNameId
                }
            }
        })

        if (topCriteriaId) {
            nextTick(() => {
                const el = criteriaRefs.value[topCriteriaId]
                if (el && mainPanel.value) {
                    const panelTop = mainPanel.value.getBoundingClientRect().top
                    const elTop = el.getBoundingClientRect().top
                    mainPanel.value.scrollBy({
                        top: elTop - panelTop,
                        behavior: 'smooth'
                    });
                }
            })
        }
    }

    function scrollToCategory(categoryId, match_search) {
        if (!match_search) {
            return;
        }

        const el = categoryRefs.value[categoryId]
        if (el && mainPanel.value) {
            const panelTop = mainPanel.value.getBoundingClientRect().top
            const elTop = el.getBoundingClientRect().top
            mainPanel.value.scrollBy({
                top: elTop - panelTop,
                behavior: 'smooth'
            })
        }
    }

    const caregiverCoffeeBaseUrl = import.meta.env.VITE_COFFEE_BASEURL + '/user/recruiter-candidates/skill/id/'

    const localCriteriaAttribute = ref({})

    watch([() => props.criteriaDescriptors, () => props.modelValue], ([criteriaDescriptors, selected]) => {
        if (criteriaDescriptors.length === 0) {
            return
        }

        localCriteriaAttribute.value = {}

        function processCriteria(targetCriteriaDescriptor) {
            localCriteriaAttribute.value[targetCriteriaDescriptor.name_id] = {attributes: {}, selected: false}

            targetCriteriaDescriptor.attributes.forEach(attributeDescriptor => {
                localCriteriaAttribute.value[targetCriteriaDescriptor.name_id]['attributes'][attributeDescriptor.name_id] = {
                    'selected_choice_ids': []
                }
            })
        }

        criteriaDescriptors.forEach(criteriaDescriptor => {
            processCriteria(criteriaDescriptor)
            if (Array.isArray(criteriaDescriptor.criterias)) {
                criteriaDescriptor.criterias.forEach(childCriteria => {
                    processCriteria(childCriteria)
                })
            }
        })

        selected.forEach(criteria => {
            localCriteriaAttribute.value[criteria.name_id]['selected'] = true
            const caregiver_parent_name_id = criteriaDescriptorNameDict.value[criteria.name_id].caregiver_parent_name_id
            if (caregiver_parent_name_id) {
                localCriteriaAttribute.value[caregiver_parent_name_id]['selected'] = true
            }
            criteria.attributes.forEach(attribute => {
                localCriteriaAttribute.value[criteria.name_id]['attributes'][attribute.name_id] = deepCopy(attribute)
                delete localCriteriaAttribute.value[criteria.name_id]['attributes'][attribute.name_id]['name_id']
            })
        })

    }, { immediate: true })

    watch([() => props.categories, () => props.criteriaDescriptors], ([categories, criterias]) => {
        const categoryCriteriaDict = {}
        categories.forEach(category => {
            categoryCriteriaDict[category.id] = []
        })

        criterias.forEach(criteria => {
            if (categoryCriteriaDict[criteria.category_id]) {
                categoryCriteriaDict[criteria.category_id].push(criteria)
            }
        })

        let tableIndex = 0
        filterCategories.value = categories.map((category) => {
            return {
                ...category,
                tableIndex: tableIndex++,
                criterias: categoryCriteriaDict[category.id].map((criteria, index) => {
                    return {
                        ...criteria,
                        tableIndex: tableIndex++
                    };
                })
            };
        })
    }, { immediate: true })

    watch(localCriteriaAttribute, (newValue) => {
        const selected = []
        Object.keys(newValue).forEach(criteriaNameId => {
            const criteria = newValue[criteriaNameId]
            if (!criteria.selected) {
                return;
            }

            const criteriaSummary = {
                name_id: criteriaNameId,
                attributes: Object.keys(criteria.attributes).map(attributeNameId => {
                    const attrObj =  {
                        name_id: attributeNameId,
                        selected_choice_ids: criteria.attributes[attributeNameId].selected_choice_ids
                    }

                    if (criteria.attributes[attributeNameId].caregiver_no_choice_selected) {
                        attrObj.caregiver_no_choice_selected = true
                    }
                    return attrObj
                })
            }

            selected.push(criteriaSummary)
        })

        if (hasChange(selected, props.modelValue) == false) {
            return;
        }
        emit('update:modelValue', selected)
    }, { immediate: true, deep: true })

    function hasChange(a, b) {
        const newValue = deepCopy(a)
        const oldValue = deepCopy(b)

        newValue.sort((a, b) => a.name_id.localeCompare(b.name_id))
        oldValue.sort((a, b) => a.name_id.localeCompare(b.name_id))
        if (newValue.length !== oldValue.length) {
            return true;
        }
        for (let i = 0; i < newValue.length; i++) {
            if (newValue[i].name_id !== oldValue[i].name_id) {
                return true;
            }
            if (newValue[i].attributes.length !== oldValue[i].attributes.length) {
                return true;
            }
            newValue[i].attributes.sort((a, b) => a.name_id.localeCompare(b.name_id))
            oldValue[i].attributes.sort((a, b) => a.name_id.localeCompare(b.name_id))
            for (let j = 0; j < newValue[i].attributes.length; j++) {
                if (newValue[i].attributes[j].name_id !== oldValue[i].attributes[j].name_id) {
                    return true
                }
                if (JSON.stringify(newValue[i].attributes[j].selected_choice_ids) !== JSON.stringify(oldValue[i].attributes[j].selected_choice_ids)) {
                    return true
                }
                if (newValue[i].attributes[j].caregiver_no_choice_selected !== oldValue[i].attributes[j].caregiver_no_choice_selected) {
                    return true
                }
            }
        }
        return false;
    }

    function checkSearchMatch(item, searchString) {
        if (!item) return false;

        if (item.label?.toLowerCase().includes(searchString)) {
            return true;
        }

        if (item.description_label?.toLowerCase().includes(searchString)) {
            return true;
        }

        if (item.choices?.some(choice => choice.label.toLowerCase().includes(searchString))) {
            return true;
        }

        if (item.criterias?.some(criteria => checkSearchMatch(criteria, searchString))) {
            return true;
        }

        if (item.attributes?.some(attribute => checkSearchMatch(attribute, searchString))) {
            return true;
        }

        return false;
    }

    watch([input, filterCategories], ([newValue, newFilterCategories]) => {
        if (newValue != null && newValue.trim() !== '') {
            const searchString = newValue.trim().toLowerCase()
            Object.keys(filterCategories.value).forEach(category => {
                let categoryMatch = false

                // Process certifications separately
                if (filterCategories.value[category].name === 'Training & Education' && props.certifications) {
                    if ('Professional Certifications'.toLowerCase().includes(searchString)) {
                        categoryMatch = true
                    }
                    if (props.certifications.some(cert => cert.toLowerCase().includes(searchString))) {
                        categoryMatch = true;
                    }
                }

                filterCategories.value[category].criterias.forEach(criteria => {
                    criteria.match_search = checkSearchMatch(criteria, searchString);

                    if (criteria.match_search) {
                        categoryMatch = true
                    }
                })
                filterCategories.value[category].match_search = categoryMatch
            })
        } else {
            Object.keys(filterCategories.value).forEach(category => {
                filterCategories.value[category].match_search = true
                filterCategories.value[category].criterias.forEach(criteria => {
                    criteria.match_search = true;
                    criteria.attributes?.forEach(attribute => {
                        attribute.match_search = true;
                        attribute.choices?.forEach(choice => {
                            choice.match_search = true;
                        });
                    });
                });
            });
        }
    }, { immediate: true });

    function handleCriteriaChange(criteria_name_id, attribute_name_id, choice_id) {
        if (attribute_name_id == null) {
            localCriteriaAttribute.value[criteria_name_id].selected = !localCriteriaAttribute.value[criteria_name_id].selected
        } else {
            localCriteriaAttribute.value[criteria_name_id].selected = true

            if (foreignLanguageCriteriaNameIds.includes(criteria_name_id)) {
                if (localCriteriaAttribute.value[criteria_name_id].attributes[attribute_name_id].selected_choice_ids.includes(choice_id)) {
                    localCriteriaAttribute.value[criteria_name_id].attributes[attribute_name_id].selected_choice_ids = localCriteriaAttribute.value[criteria_name_id].attributes[attribute_name_id].selected_choice_ids.filter(id => id !== choice_id)
                } else if (criteriaDescriptorNameDict.value[criteria_name_id].attribute_dict[attribute_name_id].can_caregiver_select_multiple === false) {
                    localCriteriaAttribute.value[criteria_name_id].attributes[attribute_name_id].selected_choice_ids = [choice_id]
                } else {
                    localCriteriaAttribute.value[criteria_name_id].attributes[attribute_name_id].selected_choice_ids.push(choice_id)
                }

                const reverseCriteriaNameId = criteria_name_id === 'foreign_language_basic' ? 'foreign_language_fluent' : 'foreign_language_basic'
                if (localCriteriaAttribute.value[reverseCriteriaNameId].attributes[attribute_name_id].selected_choice_ids.includes(choice_id)) {
                    localCriteriaAttribute.value[reverseCriteriaNameId].attributes[attribute_name_id].selected_choice_ids = localCriteriaAttribute.value[reverseCriteriaNameId].attributes[attribute_name_id].selected_choice_ids.filter(id => id !== choice_id)
                }
            } else {
                if (localCriteriaAttribute.value[criteria_name_id].attributes[attribute_name_id]) {
                    if (localCriteriaAttribute.value[criteria_name_id].attributes[attribute_name_id].selected_choice_ids.includes(choice_id)) {
                        localCriteriaAttribute.value[criteria_name_id].attributes[attribute_name_id].selected_choice_ids = localCriteriaAttribute.value[criteria_name_id].attributes[attribute_name_id].selected_choice_ids.filter(id => id !== choice_id)
                    } else if (criteriaDescriptorNameDict.value[criteria_name_id].attribute_dict[attribute_name_id].can_caregiver_select_multiple === false) {
                        localCriteriaAttribute.value[criteria_name_id].attributes[attribute_name_id].selected_choice_ids = [choice_id]
                    } else {
                        localCriteriaAttribute.value[criteria_name_id].attributes[attribute_name_id].selected_choice_ids.push(choice_id)
                    }
                } else {
                    localCriteriaAttribute.value[criteria_name_id].attributes[attribute_name_id] = {
                        selected_choice_ids: []
                    }
                }
            }
        }

        const parent_criteria_name_id = criteriaDescriptorNameDict.value[criteria_name_id].caregiver_parent_name_id
        if (parent_criteria_name_id) {
            delete localCriteriaAttribute.value[parent_criteria_name_id].attributes['configuration'].caregiver_no_choice_selected
        }

        if (attribute_name_id && localCriteriaAttribute.value[criteria_name_id].attributes[attribute_name_id].caregiver_no_choice_selected === true) {
            delete localCriteriaAttribute.value[criteria_name_id].attributes[attribute_name_id].caregiver_no_choice_selected
        }
    }

    function handleCriteriaAttributeNoChoiceChange(criteria_name_id, attribute_name_id) {
        localCriteriaAttribute.value[criteria_name_id].selected = true
        if (localCriteriaAttribute.value[criteria_name_id].attributes[attribute_name_id].caregiver_no_choice_selected) {
            delete localCriteriaAttribute.value[criteria_name_id].attributes[attribute_name_id].caregiver_no_choice_selected
        } else {
            localCriteriaAttribute.value[criteria_name_id].attributes[attribute_name_id].caregiver_no_choice_selected = true
            localCriteriaAttribute.value[criteria_name_id].attributes[attribute_name_id].selected_choice_ids = []
        }
    }

    function handleGroupCriteriaAttributeNoChoiceChange(criteria_name_id) {
        localCriteriaAttribute.value[criteria_name_id].selected = true
        if (localCriteriaAttribute.value[criteria_name_id].attributes['configuration'].caregiver_no_choice_selected === undefined) {
            localCriteriaAttribute.value[criteria_name_id].attributes['configuration'].caregiver_no_choice_selected = true
            criteriaDescriptorNameDict.value[criteria_name_id].criterias.forEach(criteria => {
                localCriteriaAttribute.value[criteria.name_id].selected = false
                criteria.attributes.forEach(attribute => {
                    localCriteriaAttribute.value[criteria.name_id].attributes[attribute.name_id].selected_choice_ids = []
                })
            })
        } else {
            delete localCriteriaAttribute.value[criteria_name_id].attributes['configuration'].caregiver_no_choice_selected
        }
    }

    function handleCriteriaAttributeAllChoiceChange(criteria_name_id, attribute_name_id) {
        localCriteriaAttribute.value[criteria_name_id].selected = true
        if (criteriaDescriptorNameDict.value[criteria_name_id].attribute_dict[attribute_name_id].can_caregiver_select_multiple === true) {
            localCriteriaAttribute.value[criteria_name_id].attributes[attribute_name_id].selected_choice_ids =
                criteriaDescriptorNameDict.value[criteria_name_id].attribute_dict[attribute_name_id].choices.map(choice => choice.id)
        }

        const parent_criteria_name_id = criteriaDescriptorNameDict.value[criteria_name_id].caregiver_parent_name_id
        if (parent_criteria_name_id) {
            delete localCriteriaAttribute.value[parent_criteria_name_id].attributes['configuration'].caregiver_no_choice_selected
        }
    }

    function handleGroupCriteriaAttributeAllChoiceChange(criteria_name_id) {
        if (criteriaDescriptorNameDict.value[criteria_name_id].criterias.length === 0) {
            return
        }

        localCriteriaAttribute.value[criteria_name_id].selected = true
        criteriaDescriptorNameDict.value[criteria_name_id].criterias.forEach(criteria => {
            localCriteriaAttribute.value[criteria.name_id].selected = true
            criteria.attributes.forEach(attribute => {
                if (localCriteriaAttribute.value[criteria.name_id].attributes[attribute.name_id].selected_choice_ids.length === 0) {
                    const choices = criteriaDescriptorNameDict.value[criteria.name_id].attribute_dict[attribute.name_id].choices
                    if (choices.length > 0) {
                        if (criteriaDescriptorNameDict.value[criteria.name_id].attribute_dict[attribute.name_id].can_caregiver_select_multiple === true) {
                            localCriteriaAttribute.value[criteria.name_id].attributes[attribute.name_id].selected_choice_ids = choices.map(choice => choice.id)
                        } else {
                            localCriteriaAttribute.value[criteria.name_id].attributes[attribute.name_id].selected_choice_ids = [choices[choices.length - 1].id]
                        }
                    }

                }
            })
        })

        if (localCriteriaAttribute.value[criteria_name_id].attributes['configuration'].caregiver_no_choice_selected === true) {
            delete localCriteriaAttribute.value[criteria_name_id].attributes['configuration'].caregiver_no_choice_selected
        }
    }

    const groupAllChoiceSelected = computed(() => {
        const dict = {}

        Object.keys(criteriaDescriptorNameDict.value).forEach(criteriaNameId => {
            if (criteriaDescriptorNameDict.value[criteriaNameId].attribute_dict['configuration']?.caregiver_all_choice_label) {
                let check = true
                criteriaDescriptorNameDict.value[criteriaNameId].criterias.forEach(criteria => {
                    if (localCriteriaAttribute.value[criteria.name_id].selected === false) {
                        check = false
                    }
                })

                dict[criteriaNameId] = check
            }
        })

        return dict
    })

    const missingRequiredAttributeSubCriterias = computed(() => {
        const missing = []
        Object.keys(localCriteriaAttribute.value).forEach(criteriaNameId => {
            if (criteriaDescriptorNameDict.value[criteriaNameId].caregiver_parent_name_id === null && criteriaNameId.includes('caregiver_group')) {
                return
            }

            const criteria = criteriaDescriptorNameDict.value[criteriaNameId]
            const missingRequiredAttributes = Object.keys(criteria.attributes).filter(attribute => {
                const attributeNameId = criteria.attributes[attribute].name_id
                const attributeDescriptor = criteriaDescriptorNameDict.value[criteriaNameId].attribute_dict[attributeNameId]

                if (attributeDescriptor.required_caregiver === false) {
                    return false
                }

                if (localCriteriaAttribute.value[criteriaNameId]['attributes'][attributeNameId].caregiver_no_choice_selected) {
                    return false
                }

                if (
                    localCriteriaAttribute.value[criteriaNameId]['attributes'][attributeNameId].selected_choice_ids == null
                    ||
                    localCriteriaAttribute.value[criteriaNameId]['attributes'][attributeNameId].selected_choice_ids == ''
                    ||
                    localCriteriaAttribute.value[criteriaNameId]['attributes'][attributeNameId].selected_choice_ids.length === 0
                ) {
                    return true
                }

                return false
            })

            if (missingRequiredAttributes.length > 0) {
                missing.push(criteriaNameId)
            }
        })

        return missing
    })

    const missingRequiredAttribute1stLevelCriterias = computed(() => {
        const missing = []
        Object.keys(criteriaDescriptorNameDict.value).forEach(criteriaNameId => {
            if (['caregiver_only', 'both'].includes(criteriaDescriptorNameDict.value[criteriaNameId].applicable_type) === false) {
                return
            }

            if (criteriaDescriptorNameDict.value[criteriaNameId].caregiver_parent_name_id !== null) {
                return
            }

            const missingRequiredAttributesChildCriterias = criteriaDescriptorNameDict.value[criteriaNameId].criterias ? criteriaDescriptorNameDict.value[criteriaNameId].criterias.filter(criteria => {
                if (missingRequiredAttributeSubCriterias.value.includes(criteria.name_id)) {
                    return true
                }
                return false
            }) : []

            if (missingRequiredAttributesChildCriterias.length > 0) {
                if (criteriaNameId.includes('caregiver_group')) {
                    if (localCriteriaAttribute.value[criteriaNameId].attributes['configuration'].caregiver_no_choice_selected === undefined) {
                        missing.push(criteriaNameId)
                    }
                } else {
                   missing.push(criteriaNameId)
                }
            }

            if (missingRequiredAttributeSubCriterias.value.includes(criteriaNameId)) {
                missing.push(criteriaNameId)
            }

            if (
                criteriaNameId.includes('caregiver_group') === true
                &&
                missing.includes(criteriaNameId) === false
                &&
                localCriteriaAttribute.value[criteriaNameId].attributes['configuration'].caregiver_no_choice_selected === undefined
            ) {
                if (localCriteriaAttribute.value[criteriaNameId].selected === false) {
                    missing.push(criteriaNameId)
                    return
                }

                let childMissingData = null
                criteriaDescriptorNameDict.value[criteriaNameId].criterias.forEach(criteria => {
                    if (localCriteriaAttribute.value[criteria.name_id].selected === true) {
                        if (Object.keys(localCriteriaAttribute.value[criteria.name_id].attributes).length === 0) {
                            childMissingData = childMissingData === null ? false : childMissingData
                        } else {
                            Object.keys(localCriteriaAttribute.value[criteria.name_id].attributes).forEach(attributeNameId => {
                                if (
                                    localCriteriaAttribute.value[criteria.name_id].attributes[attributeNameId].selected_choice_ids.length > 0
                                ) {
                                    childMissingData = childMissingData === null ? false : childMissingData
                                }

                                if (
                                    severityCriteriaNameIds.includes(criteria.name_id)
                                    &&
                                    localCriteriaAttribute.value[criteria.name_id].attributes[attributeNameId].selected_choice_ids.length === 0
                                ) {
                                    childMissingData = true
                                }
                            })
                        }
                    }
                })

                if (childMissingData === null || childMissingData) {
                    missing.push(criteriaNameId)
                }
            }
        })

        return missing
    })

    const totalRequiredCriteria = computed(() => {
        if (!localCriteriaAttribute.value || Object.keys(localCriteriaAttribute.value).length === 0) {
            return 0;
        }

        return Object.keys(criteriaDescriptorNameDict.value).filter(criteriaNameId => {
            return criteriaDescriptorNameDict.value[criteriaNameId].caregiver_parent_name_id === null &&
                (
                    criteriaDescriptorNameDict.value[criteriaNameId].applicable_type === 'caregiver_only'
                    ||
                    criteriaDescriptorNameDict.value[criteriaNameId].applicable_type === 'both'
                )
        }).length
    })

    const completedCriteria = computed(() => {
        if (!localCriteriaAttribute.value || Object.keys(localCriteriaAttribute.value).length === 0) {
            return 0;
        }

        return totalRequiredCriteria.value - missingRequiredAttribute1stLevelCriterias.value.length
    })

    const notStartedCriteria = computed(() => {
        if (!localCriteriaAttribute.value || Object.keys(localCriteriaAttribute.value).length === 0) {
            return 0;
        }

        return Object.keys(criteriaDescriptorNameDict.value).filter(criteriaNameId => {
            return criteriaDescriptorNameDict.value[criteriaNameId].caregiver_parent_name_id === null &&
                (
                    criteriaDescriptorNameDict.value[criteriaNameId].applicable_type === 'caregiver_only'
                    ||
                    criteriaDescriptorNameDict.value[criteriaNameId].applicable_type === 'both'
                ) &&
                localCriteriaAttribute.value[criteriaNameId].selected === false
        }).length
    })

    const baselineProgress = computed(() => {
        return completedCriteria.value / totalRequiredCriteria.value * 100
    })

    watch([totalRequiredCriteria, completedCriteria, notStartedCriteria], ([total, completed, notStarted]) => {
        if (!isNaN(total) &&
            total > 0 &&
            !isNaN(completed) &&
            !isNaN(notStarted)) {
            emit('update:selected-criteria-summary', total, completed, notStarted)
        }
    }, { immediate: true });


    const checkboxClasses = {
        base: 'group focus:ring-lifeworx-blue-600 focus:ring-opacity-30 hover:focus:ring-opacity-50 rounded-sm cursor-pointer text-lifeworx-blue-600 border-stone-400 group-hover:border-lifeworx-blue-600',
        checked: 'group-hover:bg-lifeworx-blue-700',
        unchecked: 'group-hover:bg-[#d8e7ee] group-hover:text-lifeworx-blue-600'
    }

    const labelClasses = {
        base: 'group pl-1.5 py-1 text-xs cursor-pointer text-lifeworx-blue-800/90 group-hover:text-lifeworx-blue-900/100 disabled:cursor-default',
        floating: 'group text-2xs font-semibold tracking-wide cursor-default group-hover:text-lifeworx-blue-900',
        checked: 'text-lifeworx-blue-900',
    }

    defineExpose({
        scrollToFirstIncomplete,
    })

</script>

<template>

    <div class="flex w-full flex-col rounded-lg">
        <div class="grid grid-cols-8">

            <div class="col-span-2 bg-lifeworx-blue-600 bg-opacity-15 border-r border-stone-300 py-5 px-3">
                <span class="inline-block italic font-semibold text-xs text-lifeworx-blue-600 ml-2 mb-2">Jump to...</span>
                <ul class="ml-5 mb-5 h-[24rem]">
                    <li
                        v-for="category in filterCategories"
                        :key="category.id"
                        :class="[
                            'py-2 leading-snug',
                            {
                                'cursor-pointer text-lifeworx-blue-800 hover:text-lifeworx-blue-600' : category.match_search === true,
                                'cursor-default text-stone-400' : category.match_search === false,
                            }
                        ]"
                        @click="scrollToCategory(category.id, category.match_search)"
                    >
                        <span
                            class="text-sm font-semibold"
                        >
                            {{ category.name }}
                        </span>
                    </li>
                </ul>
                <div
                    :class="[
                        'flex flex-col justify-center h-36 py-3 mx-5 rounded-lg gap-1',
                        {
                            'bg-lifeworx-blue-700/10 text-lifeworx-blue-950': baselineProgress < 100,
                            'hover:bg-lifeworx-red-600/10 cursor-pointer group' : baselineProgress < 100 && baselineProgress > 0,
                            'bg-lifeworx-green-600/20 text-lifeworx-green-800' : baselineProgress == 100,
                            'cursor-default' : baselineProgress == 0 || baselineProgress == 100
                        }
                    ]"
                    @click="scrollToFirstIncomplete"
                >
                    <div class="h-4 mb-1 text-center text-xs font-semibold select-none">
                        <span class="group-hover:hidden">Baseline Progress</span>
                        <span v-if="baselineProgress < 100" class="hidden group-hover:inline text-lifeworx-red-600 font-bold">
                            Next <ArrowLongRightIcon class="-mt-0.5 -mb-0.5 inline-flex h-5 w-5" aria-hidden="true" />
                        </span>
                    </div>

                    <ProgressCircle
                        :progress="baselineProgress"
                        inner_color_class="text-lifeworx-green-500"
                        outer_color_class="text-lifeworx-red-500/20"
                        :label="true"
                        :delay="100"
                    />
                </div>
            </div>

            <div class="col-span-6">
                <div class="flex p-3 rounded-tr-lg focus-within:ring-lifeworx-blue-500 focus-within:ring-1 focus-within:ring-inset border-b border-stone-300">
                    <MagnifyingGlassIcon class="flex-none inline-flex h-6 w-6 text-stone-400" />
                    <input
                        type="text"
                        v-model="input"
                        class="border-0 h-6 flex-grow w-full px-2.5 py-1.5 text-sm text-stone-700 placeholder:text-stone-400 focus:ring-0 bg-transparent"
                        id="search"
                        ref="searchInput"
                        placeholder="Start typing to search..."
                        autocomplete="off"
                    />
                    <button
                        v-if="input"
                        type="button"
                        class="text-stone-400 hover:text-lifeworx-red-500 focus:outline-none focus:ring-0"
                        @click="clearSearch()"
                    >
                        <XCircleIcon class="flex-none inline-flex h-6 w-6 -mt-1.5 -mb-1" />
                    </button>


                </div>
                <div
                    ref="mainPanel"
                    class="h-[36rem] overflow-y-scroll block"
                >
                    <table class="w-full">
                        <tbody>
                            <template
                                v-for="(category, categoryIndex) in filterCategories"
                                :key="category.id"
                            >
                                <tr
                                    v-if="category.match_search === true"
                                    :ref="el => setCategoryRef(category.id, el)"
                                    :class="[
                                        'border-b cursor-default text-lifeworx-blue-900 group',
                                        {
                                            'bg-stone-100' : category.tableIndex % 2 === 0
                                        }
                                    ]"
                                >
                                    <td
                                        colspan="4"
                                        class="text-md pl-4 py-3 font-bold text-lifeworx-blue-600"
                                    >
                                        {{ category.name }}
                                    </td>
                                </tr>
                                <template
                                    v-for="criteria in category.criterias"
                                    :key="`${category.id}-${criteria.id}`"
                                >
                                    <tr
                                        v-if="criteria.match_search === true"
                                        :ref="el => setCriteriaRef(criteria.name_id, el)"
                                        :class="[
                                            {
                                                'bg-stone-100' : criteria.tableIndex % 2 === 0,
                                                '' : criteria.tableIndex % 2 === 1
                                            }
                                        ]"
                                    >
                                        <td colspan="4">
                                            <!-- main row -->
                                            <div
                                                class="flex"
                                            >
                                                <div class="flex grow">
                                                    <h4
                                                        :class="[
                                                            'text-sm pl-8 py-2 font-bold text-lifeworx-green-500',
                                                        ]"
                                                    >
                                                        <span
                                                            :class="[
                                                                {
                                                                    'border-lifeworx-red-500 text-lifeworx-red-500' : (localCriteriaAttribute[criteria.name_id].selected === false || missingRequiredAttribute1stLevelCriterias.includes(criteria.name_id)),
                                                                }
                                                            ]"
                                                        >
                                                            {{ criteria.label }}

                                                            <ExclamationCircleIcon
                                                                v-if="localCriteriaAttribute[criteria.name_id].selected && missingRequiredAttribute1stLevelCriterias.includes(criteria.name_id)"
                                                                class="inline-flex h-5 w-5 text-lifeworx-red-500 -mt-0.5 ml-1 -mb-0.5"
                                                            />

                                                        </span>
                                                    </h4>
                                                </div>
                                            </div>
                                            <!-- end main row -->

                                            <div
                                                v-if="criteria.match_search === true && criteria.attributes.length"
                                            >
                                                <div
                                                    @click.stop
                                                    :class="[
                                                        'flex gap-5 ml-8 px-4 py-4 rounded-md mr-5 mb-5 border cursor-default border-opacity-50',
                                                        {
                                                            'border-lifeworx-red-500' : localCriteriaAttribute[criteria.name_id].selected === false || missingRequiredAttribute1stLevelCriterias.includes(criteria.name_id),
                                                            'bg-lifeworx-green-500/5 border-lifeworx-green-500' : localCriteriaAttribute[criteria.name_id].selected === true && !missingRequiredAttribute1stLevelCriterias.includes(criteria.name_id),
                                                            'bg-[#eed3cc80]' : localCriteriaAttribute[criteria.name_id].selected === true && missingRequiredAttribute1stLevelCriterias.includes(criteria.name_id),
                                                            'bg-stone-50' : localCriteriaAttribute[criteria.name_id].selected === false
                                                        },
                                                    ]"
                                                >
                                                    <div
                                                        v-if="criteria.name_id.includes('caregiver_group')"
                                                        class="w-1/4"
                                                    >
                                                        <ul
                                                            v-for="(attribute, i) in criteria.attributes"
                                                            :key="`${criteria.name_id}-${attribute.name_id}-${i}`"
                                                            class="flex flex-col gap-2 h-full min-w-28 place-content-center"
                                                        >
                                                            <li
                                                                v-if="attribute.caregiver_all_choice_label"
                                                            >
                                                                <div
                                                                    class=""
                                                                    :class="[
                                                                        'group h-fit w-fit',
                                                                        {
                                                                            'pointer-events-none': groupAllChoiceSelected[criteria.name_id]
                                                                        }
                                                                    ]"
                                                                >
                                                                    <input
                                                                        type="checkbox"
                                                                        :id="`${criteria.name_id}-configuration-all-choice`"
                                                                        @click.stop="() => handleGroupCriteriaAttributeAllChoiceChange(criteria.name_id)"
                                                                        :disabled="groupAllChoiceSelected[criteria.name_id]"
                                                                        :checked="groupAllChoiceSelected[criteria.name_id]"
                                                                        :class="[
                                                                            checkboxClasses.base,
                                                                            {
                                                                                [checkboxClasses.checked + ' text-stone-400']: groupAllChoiceSelected[criteria.name_id],
                                                                                [checkboxClasses.unchecked]: !groupAllChoiceSelected[criteria.name_id]
                                                                            }
                                                                        ]"
                                                                    />
                                                                    <label
                                                                        :for="`${criteria.name_id}-configuration-all-choice`"
                                                                        :class="[
                                                                            labelClasses.base,
                                                                            'font-bold',
                                                                            {
                                                                                [labelClasses.checked + ' text-neutral-600/80']: groupAllChoiceSelected[criteria.name_id],
                                                                            }
                                                                        ]"
                                                                    >{{ attribute.caregiver_all_choice_label }}</label>
                                                                </div>
                                                            </li>
                                                            <li
                                                                v-if="attribute.caregiver_no_choice_label"
                                                            >
                                                                <div class="group h-fit w-fit">
                                                                    <input
                                                                        type="checkbox"
                                                                        :id="`${criteria.name_id}-configuration-no-choice`"
                                                                        @click.stop="() => handleGroupCriteriaAttributeNoChoiceChange(criteria.name_id)"
                                                                        :checked="localCriteriaAttribute[criteria.name_id].attributes['configuration'].caregiver_no_choice_selected"
                                                                        :class="[
                                                                            checkboxClasses.base,
                                                                            'font-bold',
                                                                            {
                                                                                [checkboxClasses.checked]: localCriteriaAttribute[criteria.name_id].attributes['configuration'].caregiver_no_choice_selected,
                                                                                [checkboxClasses.unchecked]: !localCriteriaAttribute[criteria.name_id].attributes['configuration'].caregiver_no_choice_selected
                                                                            }
                                                                        ]"
                                                                    />
                                                                    <label
                                                                        :for="`${criteria.name_id}-configuration-no-choice`"
                                                                        :class="[
                                                                            labelClasses.base,
                                                                            'font-bold',
                                                                            {
                                                                                [labelClasses.checked]: localCriteriaAttribute[criteria.name_id].attributes['configuration'].caregiver_no_choice_selected,
                                                                            }
                                                                        ]"
                                                                    >{{ attribute.caregiver_no_choice_label }}</label>
                                                                </div>
                                                            </li>
                                                        </ul>
                                                    </div>
                                                    <template
                                                        v-if="criteria.name_id.includes('caregiver_group') === false"
                                                    >
                                                        <LatteCaregiverCriteriaAttributes
                                                            :criteria_descriptor="criteria"
                                                            :criteria_selected_attributes="localCriteriaAttribute[criteria.name_id].attributes"
                                                            :selected="localCriteriaAttribute[criteria.name_id].selected"
                                                            :criteria_descriptor_name_dict="criteriaDescriptorNameDict"
                                                            @change:criteria-attribute-choice="handleCriteriaChange"
                                                            @change:criteria-attribute-no-choice="handleCriteriaAttributeNoChoiceChange"
                                                            @change:criteria-attribute-all-choice="handleCriteriaAttributeAllChoiceChange"
                                                        />
                                                    </template>

                                                    <div
                                                        class="flex gap-x-2 gap-y-5 flex-wrap"
                                                        v-else
                                                    >
                                                        <template
                                                            v-for="(childCriteria, i) in criteria.criterias"
                                                            :key="`${criteria.name_id}-${childCriteria.name_id}-${i}`"
                                                        >
                                                            <LatteCaregiverCriteriaAttributes
                                                                :criteria_descriptor="childCriteria"
                                                                :criteria_selected_attributes="localCriteriaAttribute[childCriteria.name_id].attributes"
                                                                :selected="localCriteriaAttribute[childCriteria.name_id].selected"
                                                                :criteria_descriptor_name_dict="criteriaDescriptorNameDict"
                                                                @change:criteria-attribute-choice="handleCriteriaChange"
                                                                @change:criteria-attribute-no-choice="handleCriteriaAttributeNoChoiceChange"
                                                                @change:criteria-attribute-all-choice="handleCriteriaAttributeAllChoiceChange"
                                                            />
                                                        </template>
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                </template>
                                <template
                                    v-if="category.name == 'Training & Education'"
                                    :key="`${category.id}-certification`"
                                    >
                                    <tr
                                        v-if="category.match_search === true"
                                        :class="[
                                            {
                                                'bg-stone-100' : category.tableIndex % 2 === 0
                                            }
                                        ]"
                                    >
                                        <td colspan="4">
                                            <!-- main row -->
                                            <div class="flex">
                                                <div class="flex grow">
                                                    <h4 class="text-sm pl-8 py-2 font-bold text-lifeworx-blue-800 cursor-default">
                                                        <span title="Read-only">
                                                            Professional Certifications

                                                            <IconEditOff24Filled
                                                                class="inline-flex h-5 w-5 text-current -mt-0.5 ml-1 opacity-50"
                                                            />
                                                        </span>
                                                    </h4>
                                                </div>
                                            </div>

                                            <div
                                                class="flex flex-col items-center gap-4 ml-8 p-4 rounded-md mr-5 mb-5 bg-stone-100 border border-opacity-50 border-stone-800 cursor-default"
                                            >
                                                <div
                                                    class="flex flex-col items-center w-full"
                                                >
                                                    <ul class="flex flex-wrap justify w-full gap-x-4 pb-2">
                                                        <template
                                                            v-for="(certification, i) in props.certifications"
                                                            :key="`certification-${i}`"
                                                        >
                                                            <template v-if="caregiver?.certifications">
                                                                <!-- Show unchecked box if no matching certification -->
                                                                <li
                                                                    v-if="!caregiver.certifications.find(c => c.name === certification)"
                                                                    class="flex items-center mb-2"
                                                                >
                                                                    <input
                                                                        type="checkbox"
                                                                        :id="`certification_${i}`"
                                                                        :value="certification"
                                                                        :checked="false"
                                                                        @click.stop.prevent="() => {}"
                                                                        class="mr-1 focus:ring-0 text-stone-400 bg-stone-200 border-stone-300 rounded-sm cursor-not-allowed opacity-80 checked:bg-lifeworx-blue-700 pointer-events-none"
                                                                    />
                                                                    <label
                                                                        :for="`certification_${i}`"
                                                                        class="text-xs cursor-not-allowed text-stone-500"
                                                                    >{{ certification }}</label>
                                                                </li>
                                                                <!-- Show checked box for each matching certification -->
                                                                <li
                                                                    v-for="(matchingCert, j) in caregiver.certifications.filter(c => c.name === certification)"
                                                                    :key="`certification_${i}_${j}`"
                                                                    class="flex items-center mb-2"
                                                                >
                                                                    <input
                                                                        type="checkbox"
                                                                        :id="`certification_${i}_${j}`"
                                                                        :value="certification"
                                                                        checked
                                                                        @click.stop.prevent="() => {}"
                                                                        class="focus:ring-0 text-stone-400 bg-lifeworx-blue-600 focus:bg-lifeworx-blue-600 border-stone-300 checked:bg-lifeworx-blue-600 hover:checked:bg-lifeworx-blue-600 opacity-80 rounded-sm cursor-not-allowed"
                                                                    />
                                                                    <label
                                                                        :for="`certification_${i}_${j}`"
                                                                        class="pl-1.5 text-xs cursor-not-allowed font-semibold text-lifeworx-blue-700"
                                                                    >{{ certification }}
                                                                        <span v-if="matchingCert.state || matchingCert.status">
                                                                            {{
                                                                                [
                                                                                    matchingCert.state,
                                                                                    matchingCert.status
                                                                                ]
                                                                                .filter(item => item && item !== 'Unknown')
                                                                                .join(', ')
                                                                                ? `(${[
                                                                                    matchingCert.state,
                                                                                    matchingCert.status
                                                                                ]
                                                                                .filter(item => item && item !== 'Unknown')
                                                                                .join(', ')})`
                                                                                : ''
                                                                            }}
                                                                        </span>
                                                                    </label>
                                                                </li>
                                                            </template>
                                                        </template>
                                                    </ul>
                                                    <div class="text-xs text-stone-800 text-center">
                                                        Only active searchable certifications are included.
                                                    </div>
                                                    <a
                                                        :href="caregiverCoffeeBaseUrl + caregiver.coffee_job_profile_id"
                                                        target="_parent"
                                                        class="text-xs text-lifeworx-blue-600 hover:text-lifeworx-blue-800 underline text-center font-semibold leading-relaxed"
                                                    >
                                                        Edit this Caregiver's Certification data on the Skills tab in Coffee
                                                    </a>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                </template>
                            </template>

                        </tbody>
                    </table>
                </div>

            </div>
        </div>
    </div>
</template>
